import React, { useState, useEffect } from 'react';
import { 
  MapPin, BarChart3, Droplets, Thermometer, Cloud, 
  TrendingUp, Sprout, Search, ChevronDown, Home, Map 
} from 'lucide-react';

// CSS Styles
const styles = `
  .dashboard {
    min-height: 100vh;
    background-color: #f8fafc;
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  }

  .header {
    background-color: white;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    border-bottom: 1px solid #e2e8f0;
    padding: 1rem 0;
  }

  .header-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1rem;
  }

  .header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .logo {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .logo-icon {
    width: 48px;
    height: 48px;
    background-color: #f97316;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: bold;
    font-size: 1.25rem;
  }

  .title {
    font-size: 1.25rem;
    font-weight: 700;
    color: #1e293b;
  }

  .subtitle {
    font-size: 0.875rem;
    color: #64748b;
  }

  .project-badge {
    background-color: #06b6d4;
    color: white;
    padding: 0.25rem 1rem;
    border-radius: 999px;
    font-size: 0.875rem;
    font-weight: 500;
    margin-bottom: 0.5rem;
  }

  .main-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: #2563eb;
  }

  .department-info {
    background: linear-gradient(to right, #059669, #0284c7);
    color: white;
    padding: 0.75rem 0;
  }

  .department-text {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1rem;
    display: flex;
    justify-content: space-between;
    font-size: 0.875rem;
  }

  .department-badge {
    background-color: #10b981;
    padding: 0.25rem 1rem;
    border-radius: 4px;
  }

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 1.5rem 1rem;
  }

  .selection-panel {
    background-color: white;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.05);
    padding: 1.5rem;
    margin-bottom: 1.5rem;
  }

  .panel-title {
    font-size: 1.125rem;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .selection-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  @media (min-width: 768px) {
    .selection-grid {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  .select-container {
    margin-bottom: 0.5rem;
  }

  .select-label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: #334155;
    margin-bottom: 0.5rem;
  }

  .select-wrapper {
    position: relative;
  }

  .select-input {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    background-color: #f8fafc;
    font-size: 0.875rem;
    color: #334155;
  }

  .select-input:disabled {
    background-color: #f1f5f9;
    color: #64748b;
  }

  .select-icon {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
    pointer-events: none;
  }

  .tabs-container {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }

  .tab-button {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-weight: 500;
    transition: all 0.2s ease;
    font-size: 0.875rem;
  }

  .tab-button.active {
    background-color: #2563eb;
    color: white;
    box-shadow: 0 4px 6px rgba(37, 99, 235, 0.2);
  }

  .tab-button.inactive {
    background-color: white;
    color: #475569;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  }

  .tab-button.inactive:hover {
    background-color: #f1f5f9;
    color: #2563eb;
  }

  .main-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  @media (min-width: 1024px) {
    .main-grid {
      grid-template-columns: 2fr 1fr;
    }
  }

  .kpi-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  @media (min-width: 640px) {
    .kpi-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @media (min-width: 1024px) {
    .kpi-grid {
      grid-template-columns: repeat(4, 1fr);
    }
  }

  .kpi-card {
    background-color: white;
    border-radius: 10px;
    padding: 1.25rem;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
    border-left: 4px solid;
    display: flex;
    justify-content: space-between;
  }

  .kpi-content {
    display: flex;
    flex-direction: column;
  }

  .kpi-title {
    font-size: 0.75rem;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 0.25rem;
  }

  .kpi-value {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 0.25rem;
  }

  .kpi-subtitle {
    font-size: 0.75rem;
    color: #94a3b8;
  }

  .kpi-icon {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .data-panel {
    background-color: white;
    border-radius: 10px;
    padding: 1.5rem;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
    margin-bottom: 1.5rem;
  }

  .data-title {
    font-size: 1.125rem;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 1rem;
  }

  .table {
    width: 100%;
    border-collapse: collapse;
  }

  .table th {
    text-align: left;
    padding: 0.75rem;
    font-size: 0.75rem;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 1px solid #e2e8f0;
  }

  .table td {
    padding: 0.75rem;
    font-size: 0.875rem;
    color: #334155;
    border-bottom: 1px solid #e2e8f0;
  }

  .table tr:hover {
    background-color: #f8fafc;
  }

  .status-badge {
    padding: 0.25rem 0.5rem;
    border-radius: 999px;
    font-size: 0.75rem;
    font-weight: 500;
    display: inline-block;
  }

  .weather-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }

  @media (min-width: 640px) {
    .weather-grid {
      grid-template-columns: repeat(4, 1fr);
    }
  }

  .weather-card {
    background-color: white;
    border-radius: 8px;
    padding: 1.5rem;
    text-align: center;
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  }

  .weather-icon {
    margin: 0 auto 0.75rem;
    color: #3b82f6;
  }

  .weather-label {
    font-size: 0.75rem;
    color: #64748b;
    margin-bottom: 0.25rem;
  }

  .weather-value {
    font-size: 1.25rem;
    font-weight: 700;
    color: #1e293b;
  }

  .map-panel {
    background-color: white;
    border-radius: 10px;
    padding: 1.5rem;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
    margin-bottom: 1.5rem;
  }

  .map-container {
    height: 300px;
    background-color: #f1f5f9;
    border-radius: 8px;
    overflow: hidden;
    position: relative;
    margin-top: 1rem;
  }

  .map-placeholder {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #64748b;
  }

  .map-placeholder-icon {
    margin-bottom: 1rem;
    opacity: 0.5;
  }

  .stats-panel {
    background-color: white;
    border-radius: 10px;
    padding: 1.5rem;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  }

  .stats-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 0;
    border-bottom: 1px solid #e2e8f0;
  }

  .stats-item:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  .stats-label {
    font-size: 0.875rem;
    color: #64748b;
  }

  .stats-value {
    font-weight: 600;
    color: #1e293b;
  }

  .stats-total {
    font-weight: 700;
    color: #7c3aed;
  }

  .stats-divider {
    border-top: 1px solid #e2e8f0;
    margin: 0.75rem 0;
  }
`;

// FarmMap Component
const FarmMap = ({ selectedVillage, selectedTaluka }) => (
  <div className="map-container">
    <div className="map-placeholder">
      <MapPin size={48} className="map-placeholder-icon" />
      <p className="stats-label">Map view will appear here</p>
      <p className="stats-value">{selectedVillage}, {selectedTaluka}</p>
    </div>
  </div>
);

// Dashboard Component
const Dashboard = () => {
  const [selectedTaluka, setSelectedTaluka] = useState('');
  const [selectedVillage, setSelectedVillage] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  // Data
  const talukas = ['सांगोला', 'आटपाडी'];
  
  const villages = {
    'सांगोला': ['सांगोला', 'कुरुंदवाड', 'मोहोळ', 'तळेगाव', 'दापोली', 'धामणी', 'नंदणीं', 'वाळव्या'],
    'आटपाडी': ['आटपाडी', 'यावल', 'दसूर', 'फलटण', 'निगडी', 'महाळुंग', 'नेरली', 'मोरवाडी']
  };

  const cropData = {
    'सांगोला': [
      { crop: 'ऊस', area: '2150 हेक्टर', yield: '88%', status: 'चांगली' },
      { crop: 'सोयाबीन', area: '1850 हेक्टर', yield: '82%', status: 'चांगली' },
      { crop: 'कापूस', area: '1250 हेक्टर', yield: '75%', status: 'मध्यम' },
      { crop: 'मका', area: '950 हेक्टर', yield: '90%', status: 'उत्कृष्ट' }
    ],
    'आटपाडी': [
      { crop: 'ज्वारी', area: '1650 हेक्टर', yield: '85%', status: 'चांगली' },
      { crop: 'डाळी', area: '1200 हेक्टर', yield: '78%', status: 'मध्यम' },
      { crop: 'भुईमूग', area: '850 हेक्टर', yield: '92%', status: 'उत्कृष्ट' },
      { crop: 'तूर', area: '750 हेक्टर', yield: '86%', status: 'चांगली' }
    ]
  };

  const weatherData = {
    temperature: '32°C',
    humidity: '58%',
    rainfall: '8mm',
    windSpeed: '12 km/h'
  };

  // TabButton Component
  const TabButton = ({ id, label, icon: Icon, active }) => {
    const tabClass = active ? 'tab-button active' : 'tab-button inactive';
    return (
      <button
        onClick={() => setActiveTab(id)}
        className={tabClass}
      >
        <Icon size={18} />
        {label}
      </button>
    );
  };

  // KPICard Component
  const KPICard = ({ title, value, icon: Icon, color, subtitle }) => {
    const iconStyle = {
      backgroundColor: `${color}20`,
      color: color
    };

    const borderStyle = {
      borderLeftColor: color
    };

    return (
      <div className="kpi-card" style={borderStyle}>
        <div className="kpi-content">
          <span className="kpi-title">{title}</span>
          <span className="kpi-value">{value}</span>
          {subtitle && <span className="kpi-subtitle">{subtitle}</span>}
        </div>
        <div className="kpi-icon" style={iconStyle}>
          <Icon size={20} />
        </div>
      </div>
    );
  };

  return (
    <div className="dashboard">
      <style>{styles}</style>
      
      {/* Header */}
      <header className="header">
        <div className="header-container">
          <div className="header-content">
            <div className="logo">
              <div className="logo-icon">म</div>
              <div>
                <h1 className="title">महाराष्ट्र शासन</h1>
                <p className="subtitle">जलसंपदा विभाग</p>
              </div>
            </div>
            
            <div style={{ textAlign: 'center' }}>
              <div className="project-badge">पीक उत्पादन निरीक्षण प्रकल्प</div>
              <h2 className="main-title">CROP MONITORING SYSTEM</h2>
              <p className="subtitle">सांगली जिल्हा</p>
            </div>
            
            <div className="logo">
              <div className="logo-icon" style={{ backgroundColor: '#3b82f6' }}>ज</div>
              <div style={{ textAlign: 'right' }}>
                <p className="title">जलसंपदा विभाग</p>
                <p className="subtitle">महाराष्ट्र शासन, भारत</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Department Info */}
      <div className="department-info">
        <div className="department-text">
          <div className="department-badge">
            या टूल द्वारे पिकाची मोजणी करता येईल
          </div>
          
        </div>
      </div>

      <div className="container">
        {/* Selection Controls */}
        <div className="selection-panel">
          <h3 className="panel-title">
            <Search size={20} color="#3b82f6" />
            क्षेत्र निवड (Area Selection)
          </h3>
          
          <div className="selection-grid">
            {/* District - Fixed to Sangli */}
            <div className="select-container">
              <label className="select-label">जिल्हा (District)</label>
              <div className="select-wrapper">
                <input
                  type="text"
                  value="सांगली"
                  disabled
                  className="select-input"
                />
              </div>
            </div>

            {/* Taluka Selection */}
            <div className="select-container">
              <label className="select-label">तालुका (Taluka)</label>
              <div className="select-wrapper">
                <select
                  value={selectedTaluka}
                  onChange={(e) => {
                    setSelectedTaluka(e.target.value);
                    setSelectedVillage('');
                  }}
                  className="select-input"
                >
                  <option value="">तालुका निवडा</option>
                  {talukas.map(taluka => (
                    <option key={taluka} value={taluka}>{taluka}</option>
                  ))}
                </select>
                <ChevronDown size={16} className="select-icon" />
              </div>
            </div>

            {/* Village Selection */}
            <div className="select-container">
              <label className="select-label">गाव (Village)</label>
              <div className="select-wrapper">
                <select
                  value={selectedVillage}
                  onChange={(e) => setSelectedVillage(e.target.value)}
                  disabled={!selectedTaluka}
                  className="select-input"
                >
                  <option value="">गाव निवडा</option>
                  {selectedTaluka && villages[selectedTaluka] && villages[selectedTaluka].map(village => (
                    <option key={village} value={village}>{village}</option>
                  ))}
                </select>
                <ChevronDown size={16} className="select-icon" />
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="tabs-container">
          <TabButton
            id="overview"
            label="सारांश"
            icon={Home}
            active={activeTab === 'overview'}
          />
          <TabButton
            id="crops"
            label="पीक माहिती"
            icon={Sprout}
            active={activeTab === 'crops'}
          />
          <TabButton
            id="weather"
            label="हवामान"
            icon={Cloud}
            active={activeTab === 'weather'}
          />
          <TabButton
            id="analytics"
            label="विश्लेषण"
            icon={BarChart3}
            active={activeTab === 'analytics'}
          />
        </div>

        {/* Main Content */}
        <div className="main-grid">
          {/* Left Column - KPIs and Data */}
          <div>
            {activeTab === 'overview' && (
              <>
                {/* KPI Cards */}
                <div className="kpi-grid">
                  <KPICard
                    title="एकूण क्षेत्रफळ"
                    value="5.2K हेक्टर"
                    icon={MapPin}
                    color="#10b981"
                    subtitle="लागवड क्षेत्र"
                  />
                  <KPICard
                    title="पाणी पातळी"
                    value="68%"
                    icon={Droplets}
                    color="#3b82f6"
                    subtitle="भूजल स्तर"
                  />
                  <KPICard
                    title="पीक उत्पादन"
                    value="85%"
                    icon={TrendingUp}
                    color="#f59e0b"
                    subtitle="अपेक्षित उत्पादन"
                  />
                  <KPICard
                    title="तापमान"
                    value={weatherData.temperature}
                    icon={Thermometer}
                    color="#ef4444"
                    subtitle="वर्तमान तापमान"
                  />
                </div>

                {/* Current Taluka Info */}
                {selectedTaluka && (
                  <div className="data-panel">
                    <h3 className="data-title">
                      {selectedTaluka} तालुका - पीक माहिती
                    </h3>
                    <table className="table">
                      <thead>
                        <tr>
                          <th>पीक</th>
                          <th>क्षेत्रफळ</th>
                          <th>उत्पादनता</th>
                          <th>स्थिती</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cropData[selectedTaluka] && cropData[selectedTaluka].map((crop, index) => {
                          let statusClass = '';
                          if (crop.status === 'उत्कृष्ट') statusClass = 'background-color: #ecfdf5; color: #059669;';
                          else if (crop.status === 'चांगली') statusClass = 'background-color: #eff6ff; color: #2563eb;';
                          else statusClass = 'background-color: #fef3c7; color: #d97706;';

                          return (
                            <tr key={index}>
                              <td>{crop.crop}</td>
                              <td>{crop.area}</td>
                              <td>{crop.yield}</td>
                              <td>
                                <span className="status-badge" style={statusClass}>
                                  {crop.status}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            {activeTab === 'weather' && (
              <div className="data-panel">
                <h3 className="data-title">
                  <Cloud size={20} color="#3b82f6" style={{ marginRight: '8px' }} />
                  हवामान माहिती
                </h3>
                <div className="weather-grid">
                  <div className="weather-card">
                    <Thermometer size={32} className="weather-icon" color="#ef4444" />
                    <p className="weather-label">तापमान</p>
                    <p className="weather-value">{weatherData.temperature}</p>
                  </div>
                  <div className="weather-card">
                    <Droplets size={32} className="weather-icon" color="#3b82f6" />
                    <p className="weather-label">आर्द्रता</p>
                    <p className="weather-value">{weatherData.humidity}</p>
                  </div>
                  <div className="weather-card">
                    <Cloud size={32} className="weather-icon" color="#06b6d4" />
                    <p className="weather-label">पाऊस</p>
                    <p className="weather-value">{weatherData.rainfall}</p>
                  </div>
                  <div className="weather-card">
                    <span className="weather-icon" style={{ fontSize: '24px' }}>💨</span>
                    <p className="weather-label">वारा</p>
                    <p className="weather-value">{weatherData.windSpeed}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Map and Stats */}
          <div>
            <div className="map-panel">
              <h3 className="panel-title">
                <Map size={20} color={selectedVillage ? '#10b981' : '#94a3b8'} />
                {selectedVillage ? `${selectedVillage} - गाव नकाशा` : 'नकाशा दृश्य'}
              </h3>
              {selectedVillage ? (
                <FarmMap selectedVillage={selectedVillage} selectedTaluka={selectedTaluka} />
              ) : (
                <div className="map-container">
                  <div className="map-placeholder">
                    <MapPin size={48} className="map-placeholder-icon" />
                    <p className="stats-label">गाव निवडा</p>
                    <p className="stats-value">नकाशा पाहण्यासाठी तालुका आणि गाव निवडा</p>
                  </div>
                </div>
              )}
            </div>

            {/* Quick Stats */}
            <div className="stats-panel">
              <h3 className="panel-title">जलद आकडे</h3>
              <div className="stats-item">
                <span className="stats-label">सांगोला तालुका</span>
                <span className="stats-value" style={{ color: '#10b981' }}>6,200 हेक्टर</span>
              </div>
              <div className="stats-item">
                <span className="stats-label">आटपाडी तालुका</span>
                <span className="stats-value" style={{ color: '#3b82f6' }}>4,450 हेक्टर</span>
              </div>
              <div className="stats-divider"></div>
              <div className="stats-item">
                <span className="stats-label">एकूण क्षेत्रफळ</span>
                <span className="stats-total">10,650 हेक्टर</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;